import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, CalendarHeart, Package } from "lucide-react";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";
import PublicNav from "../components/PublicNav";
import PublicFooter from "../components/PublicFooter";
import { VENDORS } from "../lib/vendors";
import { formatRupiah as formatIDR, formatRupiahShort as formatIDRShort } from "../lib/format";

const publicApi = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL + "/api" });
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Public "Vendor & Harga" page — grid of vendor cards + featured DWG bundling card.
export default function PublicVendors() {
  const nav = useNavigate();
  const [counts, setCounts] = useState({}); // { division_id: number of PUBLISHED products }
  const [ranges, setRanges] = useState({}); // { division_id: { min, max } }
  const [bundlingCount, setBundlingCount] = useState(0);
  const [vendorImages, setVendorImages] = useState({});

  useEffect(() => {
    // Load all published to determine which vendors have items + price range
    publicApi
      .get("/public/catalog", { params: { bundling: false } })
      .then(({ data }) => {
        const c = {}, r = {};
        (data || []).forEach((p) => {
          c[p.division] = (c[p.division] || 0) + 1;
          const price = p.current_price ?? p.normal_price ?? 0;
          if (price > 0) {
            const cur = r[p.division] || { min: Infinity, max: 0 };
            r[p.division] = { min: Math.min(cur.min, price), max: Math.max(cur.max, price) };
          }
        });
        setCounts(c);
        setRanges(r);
      })
      .catch(() => {});
    publicApi
      .get("/public/catalog", { params: { bundling: true } })
      .then(({ data }) => setBundlingCount((data || []).length))
      .catch(() => {});
    publicApi.get("/public/cms/landing")
      .then(({ data }) => {
        const map = {};
        Object.entries(data?.vendor_images_resolved || {}).forEach(([slug, v]) => {
          if (v?.url) map[slug] = `${BACKEND_URL}${v.url}`;
        });
        setVendorImages(map);
      })
      .catch(() => {});
  }, []);

  const featured = useMemo(
    () => VENDORS.filter((v) => v.featured).sort((a, b) => a.order - b.order),
    []
  );
  const others = useMemo(
    () => VENDORS.filter((v) => !v.featured).sort((a, b) => a.order - b.order),
    []
  );

  return (
    <div
      className="min-h-screen bg-[#F7F1E8] text-[#1E1611] w-full max-w-full overflow-x-hidden"
      style={{ fontFamily: "'Inter', sans-serif" }}
      data-testid="public-vendors-page"
      data-public-page="true"
    >
      <style>{`
        .font-serif { font-family: 'Cormorant Garamond', serif; }
        .gold-line { background: linear-gradient(90deg, transparent 0%, #C4A57B 20%, #C4A57B 80%, transparent 100%); height: 1px; }
        .luxury-shadow { box-shadow: 0 20px 60px -20px rgba(139, 90, 42, 0.35); }
      `}</style>

      <Seo title="Vendor & Harga — MUA, Dekorasi, Foto, Attire, Musik & WO" path="/vendor"
        description="Lihat katalog & harga setiap vendor Dessy Wedding Group: MUA, dekorasi, fotografi & video, attire, musik, wedding organizer, handcraft dan lainnya di Garut & Bandung." />
      <PublicNav />

      {/* Hero */}
      <section className="border-b border-[#E5D9C4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-20 text-center">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-8 h-px bg-[#C4A57B]" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#8B5A2A] font-semibold">
              Vendor & Harga
            </span>
            <div className="w-8 h-px bg-[#C4A57B]" />
          </div>
          <h1 className="font-serif font-medium text-[36px] md:text-[56px] leading-[1.05] tracking-tight">
            Pilih <span className="italic text-[#8B5A2A]">vendor</span> untuk melihat paket & harga
          </h1>
          <div className="mt-4 gold-line max-w-xs mx-auto" />
          <p className="mt-5 max-w-2xl mx-auto text-[#5C4A3D] font-light text-sm md:text-base">
            Setiap vendor punya katalog paket sendiri. Klik "Lihat Paket & Harga"
            di kartu vendor untuk melihat daftar paket dan filter berdasarkan
            jenis acara.
          </p>
        </div>
      </section>

      {/* Featured DWG Bundling card */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-10">
        <Link
          to="/paket-wedding-dwg"
          className="block relative overflow-hidden rounded-3xl luxury-shadow group"
          data-testid="vendor-bundling-card"
        >
          <img
            src="https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1600&q=80"
            alt="Paket Wedding DWG"
            className="w-full h-56 md:h-72 object-cover group-hover:scale-105 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1E1611]/85 via-[#1E1611]/50 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="p-6 md:p-12 text-white max-w-2xl">
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] bg-[#C4A57B] text-[#1E1611] px-3 py-1.5 rounded-full font-bold">
                <Package className="w-3.5 h-3.5" />
                Paket Bundling DWG
              </span>
              <h2 className="font-serif text-3xl md:text-5xl mt-3 leading-tight">
                Paket Wedding DWG
              </h2>
              <p className="mt-3 text-[#E5D9C4] font-light text-sm md:text-base">
                Bundling lengkap CV Dessy Wedding Group — gabungan MUA, dekorasi,
                foto/video, hingga organizer dalam satu paket. {bundlingCount > 0 && (
                  <span className="text-[#C4A57B] font-semibold">
                    {bundlingCount} paket tersedia.
                  </span>
                )}
              </p>
              <div className="mt-5 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-[#C4A57B] group-hover:text-white transition-colors">
                Lihat Paket Bundling <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </div>
          </div>
        </Link>
      </section>

      {/* Vendor grid — Featured */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-14">
        <div className="mb-6 flex items-center gap-3">
          <div className="w-6 h-px bg-[#8B5A2A]" />
          <h3 className="font-serif text-2xl italic text-[#8B5A2A]">
            Vendor Utama
          </h3>
          <div className="flex-1 h-px bg-[#E5D9C4]" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((v, i) => (
            <Reveal key={v.slug} delay={(i % 3) * 0.08}>
            <VendorCard
              v={v}
              image={vendorImages[v.slug] || v.image}
              count={counts[v.division_id] || 0}
              range={ranges[v.division_id]}
              onClick={() =>
                (counts[v.division_id] || 0) > 0
                  ? nav(`/vendor/${v.slug}`)
                  : nav(`/consultation?vendor=${encodeURIComponent(v.name)}`)
              }
              testid={`vendor-card-${v.slug}`}
            />
            </Reveal>
          ))}
        </div>
      </section>

      {/* Vendor grid — Others */}
      {others.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-14">
          <div className="mb-6 flex items-center gap-3">
            <div className="w-6 h-px bg-[#8B5A2A]" />
            <h3 className="font-serif text-2xl italic text-[#8B5A2A]">
              Vendor Lainnya
            </h3>
            <div className="flex-1 h-px bg-[#E5D9C4]" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {others.map((v, i) => (
              <Reveal key={v.slug} delay={(i % 3) * 0.08}>
              <VendorCard
                v={v}
                image={vendorImages[v.slug] || v.image}
                count={counts[v.division_id] || 0}
                range={ranges[v.division_id]}
                onClick={() =>
                  (counts[v.division_id] || 0) > 0
                    ? nav(`/vendor/${v.slug}`)
                    : nav(`/consultation?vendor=${encodeURIComponent(v.name)}`)
                }
                testid={`vendor-card-${v.slug}`}
              />
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* CTA Konsultasi */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="rounded-3xl bg-[#1E1611] text-[#F7F1E8] px-8 py-12 md:px-14 md:py-16 text-center">
          <CalendarHeart className="w-8 h-8 mx-auto mb-3 text-[#C4A57B]" />
          <h3 className="font-serif text-3xl md:text-4xl leading-tight">
            Butuh kombinasi vendor tertentu?
          </h3>
          <p className="mt-3 text-[#E5D9C4] font-light max-w-xl mx-auto text-sm">
            Cerita kebutuhan Anda lewat Konsultasi Online — tim kami bantu
            menyusun kombinasi vendor dan estimasi biaya.
          </p>
          <button
            onClick={() => nav("/consultation")}
            className="mt-6 inline-flex items-center gap-2 bg-[#C4A57B] text-[#1E1611] hover:bg-white transition-colors px-8 py-3.5 rounded-full font-semibold text-xs uppercase tracking-widest"
            data-testid="vendors-cta-consult"
          >
            Mulai Konsultasi <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

function VendorCard({ v, image, count, range, onClick, testid }) {
  const soon = count === 0;
  return (
    <div
      className="bg-white rounded-2xl overflow-hidden luxury-shadow hover:-translate-y-1 transition-all duration-500 group flex flex-col h-full"
      data-testid={testid}
    >
      <div className="relative h-44 overflow-hidden">
        <img
          src={image || v.image}
          alt={v.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
        <div className="absolute top-3 left-3">
          {soon ? (
            <span className="text-[9px] uppercase tracking-[0.2em] bg-[#8C7A5C] text-white px-3 py-1.5 rounded-full font-semibold">
              Segera Hadir
            </span>
          ) : (
            <span className="text-[9px] uppercase tracking-[0.2em] bg-white/95 text-[#1E1611] px-3 py-1.5 rounded-full font-semibold">
              {count} paket
            </span>
          )}
        </div>
        <div className="absolute bottom-3 left-4 right-4 text-white">
          <div className="text-[10px] uppercase tracking-widest text-[#C4A57B]">
            {v.tagline}
          </div>
          <div className="font-serif text-2xl leading-tight mt-0.5">{v.name}</div>
        </div>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <p className="text-sm text-[#5C4A3D] font-light flex-1">{v.description}</p>
        {range && !soon && (
          <div className="mt-4 pt-4 border-t border-[#F0EBE0]" data-testid={`${testid}-range`}>
            <div className="flex items-end justify-between gap-3">
              <div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-[#8B5A2A] font-semibold">Mulai dari</div>
                <div className="font-serif text-2xl text-[#1E1611] leading-tight">{formatIDR(range.min)}</div>
              </div>
              {range.max > range.min && (
                <div className="text-right">
                  <div className="text-[9px] uppercase tracking-[0.2em] text-[#8C7A5C] font-semibold">Hingga</div>
                  <div className="text-sm text-[#5C4A3D]">{formatIDRShort(range.max)}</div>
                </div>
              )}
            </div>
          </div>
        )}
        <button
          onClick={onClick}
          className={`mt-5 inline-flex items-center justify-center gap-2 rounded-full text-[11px] font-semibold uppercase tracking-widest py-3 transition-colors ${
            soon
              ? "bg-white border border-[#8B5A2A] text-[#8B5A2A] hover:bg-[#8B5A2A] hover:text-white"
              : "bg-[#1E1611] text-white hover:bg-[#8B5A2A]"
          } mt-4`}
          data-testid={`${testid}-cta`}
        >
          {soon ? (
            <>
              <CalendarHeart className="w-3.5 h-3.5" /> Konsultasi Vendor Ini
            </>
          ) : (
            <>
              Lihat Paket & Harga <ArrowRight className="w-3.5 h-3.5" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}
